/**
 * ImageLightbox — Modal viewer per immagini con zoom e navigazione
 * Notion-style design
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ZoomIn, ZoomOut, Download, ChevronLeft, ChevronRight, RotateCw } from 'lucide-react';

interface ImageLightboxProps {
  images: { url: string; name?: string }[];
  initialIndex: number;
  onClose: () => void;
}

export function ImageLightbox({ images, initialIndex, onClose }: ImageLightboxProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [scale, setScale] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef({ x: 0, y: 0 });
  const translateStart = useRef({ x: 0, y: 0 });

  const current = images[currentIndex];
  const hasMultiple = images.length > 1;

  const resetTransform = useCallback(() => {
    setScale(1);
    setRotation(0);
    setTranslate({ x: 0, y: 0 });
  }, []);

  const goNext = useCallback(() => {
    if (currentIndex < images.length - 1) {
      setCurrentIndex(i => i + 1);
      resetTransform();
    }
  }, [currentIndex, images.length, resetTransform]);

  const goPrev = useCallback(() => {
    if (currentIndex > 0) {
      setCurrentIndex(i => i - 1);
      resetTransform();
    }
  }, [currentIndex, resetTransform]);

  const zoomIn = useCallback(() => setScale(s => Math.min(s * 1.3, 5)), []);
  const zoomOut = useCallback(() => {
    setScale(s => {
      const next = Math.max(s / 1.3, 0.5);
      if (next <= 1) setTranslate({ x: 0, y: 0 });
      return next;
    });
  }, []);
  const rotate = useCallback(() => setRotation(r => (r + 90) % 360), []);

  // Keyboard
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') goNext();
      if (e.key === 'ArrowLeft') goPrev();
      if (e.key === '+' || e.key === '=') zoomIn();
      if (e.key === '-') zoomOut();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose, goNext, goPrev, zoomIn, zoomOut]);

  // Wheel zoom
  const handleWheel = useCallback((e: React.WheelEvent) => {
    e.preventDefault();
    if (e.deltaY < 0) zoomIn();
    else zoomOut();
  }, [zoomIn, zoomOut]);

  // Drag to pan
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (scale <= 1) return;
    e.preventDefault();
    setDragging(true);
    dragStart.current = { x: e.clientX, y: e.clientY };
    translateStart.current = { ...translate };
  }, [scale, translate]);

  useEffect(() => {
    if (!dragging) return;
    const handleMove = (e: MouseEvent) => {
      setTranslate({
        x: translateStart.current.x + (e.clientX - dragStart.current.x),
        y: translateStart.current.y + (e.clientY - dragStart.current.y),
      });
    };
    const handleUp = () => setDragging(false);
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [dragging]);

  // Double click to toggle zoom
  const handleDoubleClick = useCallback(() => {
    if (scale > 1) {
      setScale(1);
      setTranslate({ x: 0, y: 0 });
    } else {
      setScale(2.5);
    }
  }, [scale]);

  const handleDownload = useCallback(() => {
    const a = document.createElement('a');
    a.href = current.url;
    a.download = current.name || 'image';
    a.target = '_blank';
    a.click();
  }, [current]);

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex flex-col"
      style={{ backgroundColor: 'rgba(0,0,0,0.92)' }}
    >
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-3 flex-shrink-0">
        <div className="flex items-center gap-1 text-white/70 text-sm">
          {hasMultiple && <span>{currentIndex + 1} / {images.length}</span>}
          {current.name && <span className="ml-2 truncate max-w-[300px]">{current.name}</span>}
        </div>
        <div className="flex items-center gap-1">
          <button onClick={zoomOut} className="p-2 rounded-lg hover:bg-white/10 text-white/70 hover:text-white transition-colors" title="Zoom out">
            <ZoomOut className="w-5 h-5" strokeWidth={1.5} />
          </button>
          <span className="text-white/50 text-xs min-w-[40px] text-center">{Math.round(scale * 100)}%</span>
          <button onClick={zoomIn} className="p-2 rounded-lg hover:bg-white/10 text-white/70 hover:text-white transition-colors" title="Zoom in">
            <ZoomIn className="w-5 h-5" strokeWidth={1.5} />
          </button>
          <button onClick={rotate} className="p-2 rounded-lg hover:bg-white/10 text-white/70 hover:text-white transition-colors" title="Ruota">
            <RotateCw className="w-5 h-5" strokeWidth={1.5} />
          </button>
          <button onClick={handleDownload} className="p-2 rounded-lg hover:bg-white/10 text-white/70 hover:text-white transition-colors" title="Scarica">
            <Download className="w-5 h-5" strokeWidth={1.5} />
          </button>
          <div className="w-px h-5 bg-white/20 mx-1" />
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-white/10 text-white/70 hover:text-white transition-colors" title="Chiudi (Esc)">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {/* Image area */}
      <div
        className="flex-1 flex items-center justify-center overflow-hidden relative"
        onWheel={handleWheel}
        style={{ cursor: scale > 1 ? (dragging ? 'grabbing' : 'grab') : 'default' }}
      >
        {/* Prev/Next arrows */}
        {hasMultiple && currentIndex > 0 && (
          <button
            onClick={goPrev}
            className="absolute left-4 z-10 p-3 rounded-full bg-black/40 hover:bg-black/60 text-white/80 hover:text-white transition-colors"
          >
            <ChevronLeft className="w-6 h-6" strokeWidth={1.5} />
          </button>
        )}
        {hasMultiple && currentIndex < images.length - 1 && (
          <button
            onClick={goNext}
            className="absolute right-4 z-10 p-3 rounded-full bg-black/40 hover:bg-black/60 text-white/80 hover:text-white transition-colors"
          >
            <ChevronRight className="w-6 h-6" strokeWidth={1.5} />
          </button>
        )}

        <img
          src={current.url}
          alt={current.name || ''}
          className="max-w-[90vw] max-h-[85vh] object-contain select-none"
          style={{
            transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale}) rotate(${rotation}deg)`,
            transition: dragging ? 'none' : 'transform 0.2s ease',
          }}
          onMouseDown={handleMouseDown}
          onDoubleClick={handleDoubleClick}
          draggable={false}
        />
      </div>

      {/* Thumbnails strip */}
      {hasMultiple && (
        <div className="flex items-center justify-center gap-2 px-4 py-3 flex-shrink-0">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => { setCurrentIndex(i); resetTransform(); }}
              className="rounded-lg overflow-hidden flex-shrink-0 transition-all"
              style={{
                width: 48,
                height: 48,
                border: i === currentIndex ? '2px solid #2383E2' : '2px solid transparent',
                opacity: i === currentIndex ? 1 : 0.5,
              }}
            >
              <img src={img.url} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {/* Click backdrop to close */}
      <div
        className="absolute inset-0 -z-10"
        onClick={onClose}
      />
    </div>,
    document.body
  );
}
